import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Role } from '../types/roles';
import RolesList from './RolesList';
import RoleForm from './RoleForm';
import RoleModal from './RoleModal';

interface RolesSectionProps {
  roles: Role[];
  selectedRole: Role | null;
  roleName: string;
  setRoleName: (name: string) => void;
  roleDescription: string;
  setRoleDescription: (description: string) => void;
  onSelectRole: (role: Role) => void;
  onCreateRole: (name: string, description: string) => Promise<void>;
  onUpdateRole: () => Promise<void>;
  onDeleteRole: () => Promise<void>;
}

const RolesSection: React.FC<RolesSectionProps> = ({
  roles,
  selectedRole,
  roleName,
  setRoleName,
  roleDescription,
  setRoleDescription,
  onSelectRole,
  onCreateRole,
  onUpdateRole,
  onDeleteRole,
}) => {
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleCreateRole = async (name: string, description: string) => {
    await onCreateRole(name, description);
    handleCloseModal();
  };

  return (
    <>
      <Typography variant="h6" sx={{ color: (theme) => theme.palette.custom.titleText }}>
        Roles
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Box sx={{ width: '40%' }}>
          <RolesList
            roles={roles}
            selectedRole={selectedRole}
            onSelectRole={onSelectRole}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'uppercase', bgcolor: (theme) => theme.palette.custom.buttonBackground }}
            >
              New
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Delete />}
              onClick={onDeleteRole}
              disabled={!selectedRole}
              sx={{ textTransform: 'uppercase', bgcolor: (theme) => theme.palette.custom.buttonBackground }}
            >
              Delete
            </Button>
          </Box>
        </Box>

        <Box sx={{ width: '60%' }}>
          <RoleForm
            selectedRole={selectedRole}
            roleName={roleName}
            setRoleName={setRoleName}
            roleDescription={roleDescription}
            setRoleDescription={setRoleDescription}
            onUpdate={onUpdateRole}
          />
        </Box>
      </Box>

      <RoleModal
        open={openModal}
        onClose={handleCloseModal}
        onSave={handleCreateRole}
      />
    </>
  );
};

export default RolesSection;